import { useState, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import { Props_SnippetImageCarousel } from "./ImageCarousel.props";

const SnippetImageCarousel = (props: Props_SnippetImageCarousel) => {
    const { heading, repeatable_image } = props;
    const [isPlaying, setIsPlaying] = useState(true);
    const swiperRef = useRef<SwiperType | null>(null);

    const snippetIDRef = useRef<string>(
        Math.random().toString(36).substring(2, 10)
    );
    const snippetID = snippetIDRef.current;

    const classHeadingVisibility: string = heading.visibility.value
        ? `snippet-image-carousel__heading--${heading.visibility.value}`
        : "";

    const togglePlayPause = () => {
        if (!swiperRef.current) return;

        if (isPlaying) {
            swiperRef.current.autoplay.stop();
        } else {
            swiperRef.current.autoplay.start();
        }
        setIsPlaying(!isPlaying);
    };

    const handleMouseEnter = () => {
        if (swiperRef.current && isPlaying) {
            swiperRef.current.autoplay.stop();
        }
    };

    const handleMouseLeave = () => {
        if (swiperRef.current && isPlaying) {
            swiperRef.current.autoplay.start();
        }
    };

    return (
        <section className="snippet-image-carousel">
            <div className="snippet-image-carousel__container">
                {heading?.content?.value && (
                    <h2
                        id={`snippet-image-carousel-${snippetID}`}
                        className={`snippet-image-carousel__heading ${classHeadingVisibility}`}
                    >
                        {heading.content.value}
                    </h2>
                )}

                <div
                    className="snippet-image-carousel__slider"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <Swiper
                        onSwiper={(swiper) => {
                            swiperRef.current = swiper;
                        }}
                        modules={[Autoplay, Navigation]}
                        autoplay={{
                            delay: 3000,
                            disableOnInteraction: false,
                            pauseOnMouseEnter: true,
                        }}
                        loop={true}
                        speed={800}
                        navigation={{
                            nextEl: ".swiper-button-next",
                            prevEl: ".swiper-button-prev",
                        }}
                        slidesPerView="auto"
                        spaceBetween={80}
                        grabCursor={true}
                        watchSlidesProgress={true}
                        className="snippet-image-carousel__swiper"
                    >
                        {repeatable_image.map((imageItem, index) => {
                            const { image, alt } = imageItem;

                            return (
                                <SwiperSlide
                                    key={`snippet-image-carousel-${snippetID}-${index}`}
                                    className="snippet-image-carousel__slide"
                                >
                                    {image?.value && (
                                        <img
                                            src={image.value}
                                            alt={alt?.value || ""}
                                            className="snippet-image-carousel__image"
                                        />
                                    )}
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                </div>

                <div className="snippet-image-carousel__controls">
                    <button
                        className={`snippet-image-carousel__play-pause ${isPlaying ? "snippet-image-carousel__play-pause--playing" : "snippet-image-carousel__play-pause--paused"}`}
                        onClick={togglePlayPause}
                        aria-label={
                            isPlaying ? "Pause carousel" : "Play carousel"
                        }
                        aria-pressed={!isPlaying}
                    >
                        {isPlaying ? (
                            <span className="snippet-image-carousel__pause-icon">
                            </span>
                        ) : (
                            <span className="snippet-image-carousel__play-icon">
                            </span>
                        )}
                    </button>
                </div>
            </div>
        </section>
    );
};

export default SnippetImageCarousel;
